import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CopyTooltip from '../Editor/utils/CopyTooltip';
import SectionModal from '../Editor/utils/SectionModal';
import PushToEditor from '../Editor/utils/PushToEditor';
import { showToaster, toasterTypes } from '../../widgets';

export function ResearchReportDisplay({ reportData, download, setDownload }) {
    const citationRefs = useRef({});
    const reportRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [selectedText, setSelectedText] = useState('');
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [showTooltip, setShowTooltip] = useState(false);
    const [action, setAction] = useState<string>("");

    const scrollToCitation = (anchorId) => {
        const el = citationRefs.current[anchorId];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        if (!reportData || !reportData.citations?.length) return;

        const hash = window.location.hash;
        if (hash.startsWith('#ref-')) {
            const anchorId = hash.slice(1);
            requestAnimationFrame(() => scrollToCitation(anchorId));
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [reportData]);

    useEffect(() => {
        if (download) {
            handleDownloadPdf();
        }
    }, [download])

    const handleDownloadPdf = useCallback(async () => {
        if (!reportRef.current || isDownloading) return;

        try {
            setIsDownloading(true);

            // Hide the download button temporarily
            const downloadButton: any = document.querySelector('.download-button');
            const originalDisplay = downloadButton ? downloadButton.style.display : null;
            if (downloadButton) {
                downloadButton.style.display = 'none';
            }

            // Wait a bit for any dynamic content to render
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get the element's dimensions
            const element: any = reportRef.current;
            const rect: any = element.getBoundingClientRect();

            // Capture the content
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: rect.width,
                height: rect.height,
                scrollX: 0,
                scrollY: 0,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
            });

            // Calculate PDF dimensions (A4 size in points)
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Create PDF
            const pdf = new jsPDF({
                orientation: imgHeight > pageHeight ? 'portrait' : 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            // If content is longer than one page, we need to split it
            if (imgHeight > pageHeight) {
                let position = 0;
                const pageData = canvas.getContext('2d');
                const topPadding = 10; // padding in mm for top of each page

                while (position < canvas.height) {
                    // Calculate the portion of the image for this page
                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = canvas.width;
                    pageCanvas.height = Math.min(canvas.height - position, (pageHeight * canvas.width) / imgWidth);

                    const pageContext: any = pageCanvas.getContext('2d');
                    pageContext.drawImage(canvas, 0, position, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);

                    const pageImgData = pageCanvas.toDataURL('image/png');

                    if (position > 0) {
                        pdf.addPage();
                    }

                    // Add top padding for subsequent pages
                    const yPosition = position > 0 ? topPadding : 0;
                    const availableHeight = position > 0 ? pageHeight - topPadding : pageHeight;
                    const imageHeight = Math.min((pageCanvas.height * imgWidth) / pageCanvas.width, availableHeight);

                    pdf.addImage(pageImgData, 'PNG', 0, yPosition, imgWidth, imageHeight);
                    position += pageCanvas.height;
                }
            } else {
                // Single page
                const imgData = canvas.toDataURL('image/png');
                pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            }

            // Save the PDF
            const filename = `${reportData?.title?.replace(/[^a-z0-9]/gi, '_') || 'research-report'}.pdf`;
            pdf.save(filename);

            // Restore the download button
            if (downloadButton && originalDisplay !== null) {
                downloadButton.style.display = originalDisplay;
            }

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        } finally {
            setIsDownloading(false);
            setDownload(false);
        }
    }, [reportData, isDownloading]);

    /**
     * function call to Push code to editor
     */
    const addToEditor = (data) => {
        console.log(data)
        let Obj = {
            "type": "paragraph",
            "attrs": {
                "id": "",
                "class": "paragraph",
                "track": [],
                "group": "",
                "viewid": ""
            },
            "content": [
                {
                    "type": "text",
                    "text": selectedText
                }
            ]
        };
        let response = PushToEditor(Obj, data.section.value)
        if (response) {
            showToaster(toasterTypes.SUCCESS, "Pushed code to editor")
        }
        setAction("")
    }

    useEffect(() => {
        // Attach the mouseup event listener to the document
        document.addEventListener('mouseup', getSelectedText);

        // Clean up the event listener on component unmount
        return () => {
            document.removeEventListener('mouseup', getSelectedText);
        };
    }, [selectedText]);

    /**
     * 
     * @param data 
     */
    const getSelectedText = (event) => {
        const selection : any = window.getSelection();
        if (selection.toString().trim()) {
            // Update the state with the selected text and tooltip position
            setSelectedText(selection.toString());
            setTooltipStyle({
                top: `${event.clientY + window.scrollY + 10}px`,
                left: `${event.clientX + window.scrollX + 10}px`,
            });

            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    if (!reportData) {
        return <p>Loading report...</p>;
    }

    return (
        <>
            {
                action === "section" &&
                <SectionModal pushAction={addToEditor} onclose={() => setAction("")} />
            }
            {action === "" && <div className="research-report" ref={reportRef}>
                <div className="report-header">
                    <h1>{reportData.title}</h1>
                </div>

                <div className="report-content">
                    <div className="report-summary">
                        <h2>Summary</h2>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                a: ({ href, children }) => {
                                    const isAnchor = href?.startsWith('#');
                                    const anchorId = href?.slice(1);

                                    const handleClick = (e) => {
                                        e.preventDefault();
                                        if (isAnchor && anchorId) {
                                            scrollToCitation(anchorId);
                                        }
                                    };

                                    return (
                                        <a href={href} onClick={handleClick}>
                                            {children}
                                        </a>
                                    );
                                }
                            }}
                        >
                            {reportData.summary_markdown}
                        </ReactMarkdown>
                    </div>

                    <div className="report-sections">
                        <h2>Sections</h2>
                        {reportData.sections.map((section, index) => (
                            <section key={index} aria-labelledby={`section-heading-${index}`}>
                                <h3 id={`section-heading-${index}`}>{section.heading}</h3>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        a: ({ href, children }) => {
                                            const isAnchor = href?.startsWith('#');
                                            const anchorId = href?.slice(1);

                                            const handleClick = (e) => {
                                                e.preventDefault();
                                                if (isAnchor && anchorId) {
                                                    scrollToCitation(anchorId);
                                                }
                                            };

                                            return (
                                                <a href={href} onClick={handleClick}>
                                                    {children}
                                                </a>
                                            );
                                        }
                                    }}
                                >
                                    {section.content_markdown}
                                </ReactMarkdown>
                            </section>
                        ))}
                    </div>

                    <div className="report-citations">
                        <h2>References</h2>
                        <ul>
                            {reportData.citations.map((citation) => (
                                <li key={citation.anchor_id} ref={el => citationRefs.current[citation.anchor_id] = el}>
                                    <strong>[{citation.id}]</strong> {citation.title}
                                    {citation.authors && citation.authors.length > 0 && ` by ${citation.authors.join(', ')}`}
                                    {citation.year && ` (${citation.year})`}.
                                    Available at: <a href={citation.url} target="_blank" rel="noopener noreferrer">{citation.source_name || citation.url}</a>
                                    {citation.accessed_date && ` (Accessed: ${citation.accessed_date})`}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="report-conclusion">
                        <h2>Conclusion</h2>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                a: ({ href, children }) => {
                                    const isAnchor = href?.startsWith('#');
                                    const anchorId = href?.slice(1);

                                    const handleClick = (e) => {
                                        e.preventDefault();
                                        if (isAnchor && anchorId) {
                                            scrollToCitation(anchorId);
                                        }
                                    };

                                    return (
                                        <a href={href} onClick={handleClick}>
                                            {children}
                                        </a>
                                    );
                                }
                            }}
                        >
                            {reportData.conclusion_markdown}
                        </ReactMarkdown>
                    </div>

                    <p><small>Report generated at: {new Date(reportData.report_generated_at).toLocaleString()}</small></p>
                </div>
            </div>}
            {showTooltip && (
                <CopyTooltip tooltipStyle={tooltipStyle} data={selectedText} pushAction={() => setAction("section")} />
            )}
        </>
    );
}